/**
 * WorkspaceActivityStream — chronological log of every meaningful
 * mutation in a workspace.
 *
 * The "git log for the workspace" surface. Renders as a vertical
 * timeline newest-first with entries like:
 *   "AI tracked HR 28 — 11:42am"
 *   "User added Sarah Connor as a contact at Manhattan Institute — 11:38am"
 *   "AI designated Sen. Lee as champion of Pro-Life — 11:30am"
 *
 * Lives on /app/dashboard below the AI feed. Per-entity portal pages
 * can also mount this with a subject filter to show "everything that
 * happened with Lee" in his sidebar.
 *
 * Auto-refreshes via tanstack-query — the broadcast in
 * src/workspace-events.ts fires data_changed on every write, the
 * useBrowserBus hook invalidates the ['workspace-activity', wsId] key,
 * and the timeline re-renders within the same React render cycle.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Bot, ChevronDown, ChevronRight, Clock, Settings, User, type LucideIcon } from "lucide-react";
import { DataUnavailable } from "./DataUnavailable.js";

export interface WorkspaceEvent {
  id: number;
  workspace_id: string;
  event_type: string;
  actor_type: "user" | "ai" | "system" | "scheduled" | "external";
  actor_user_id: string | null;
  mediator: string | null;
  subject_type: string | null;
  subject_id: string | null;
  subject_label: string | null;
  related_type: string | null;
  related_id: string | null;
  related_label: string | null;
  summary: string;
  detail: string | null;
  payload: Record<string, unknown>;
  chat_id: string | null;
  created_at: string;
}

export interface WorkspaceActivityStreamProps {
  /** Workspace whose events to show. Pass null to render nothing. */
  workspaceId: string | null | undefined;
  /** Limit the number of events shown. Default 30. */
  limit?: number;
  /** Optional filter — show only events matching this subject. Used by
   *  per-entity portal pages to render an entity-scoped history. */
  subjectType?: string;
  subjectId?: string;
  /** Optional filter — only AI / only user / etc. */
  actorType?: WorkspaceEvent["actor_type"];
  /** Heading shown above the stream. Default "Recent activity". */
  heading?: string;
  /** When true, the stream collapses to its header by default with a
   *  toggle to expand. Useful below the AI feed where it competes for
   *  space. Default false. */
  collapsible?: boolean;
}

const ACTOR_BADGE: Record<WorkspaceEvent["actor_type"], { label: string; cls: string; icon: LucideIcon }> = {
  ai: { label: "AI", cls: "bg-accent/60 text-ink", icon: Bot },
  user: { label: "you", cls: "bg-stone-100 text-stone-700", icon: User },
  system: { label: "system", cls: "bg-stone-100 text-stone-600", icon: Settings },
  scheduled: { label: "scheduled", cls: "bg-warn-soft text-warn", icon: Clock },
  external: { label: "external", cls: "bg-info-soft text-info", icon: ArrowUpRight },
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(1, Math.floor((now - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Map a subject_type to a route. Returns null for types without a
 *  known route (donation, briefing, etc.) — those entries don't link. */
function entityHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  switch (type) {
    case "member": return `/app/members/${encodeURIComponent(id)}`;
    case "bill": return `/app/bills/${encodeURIComponent(id)}`;
    case "contact": return `/app/contacts/${encodeURIComponent(id)}`;
    case "account":
    case "organization": return `/app/accounts/${encodeURIComponent(id)}`;
    case "issue": return `/app/issues/${encodeURIComponent(id)}`;
    case "coalition": return `/app/coalitions/${encodeURIComponent(id)}`;
    default: return null;
  }
}

export function WorkspaceActivityStream({
  workspaceId,
  limit = 30,
  subjectType,
  subjectId,
  actorType,
  heading = "Recent activity",
  collapsible = false,
}: WorkspaceActivityStreamProps) {
  const [expanded, setExpanded] = useState(!collapsible);

  const { data: events = [], isLoading, error, refetch } = useQuery<WorkspaceEvent[]>({
    queryKey: ["workspace-activity", workspaceId, limit, subjectType, subjectId, actorType],
    enabled: !!workspaceId,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (subjectType) params.set("subject_type", subjectType);
      if (subjectId) params.set("subject_id", subjectId);
      if (actorType) params.set("actor_type", actorType);
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/activity?${params}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load workspace activity");
      const data = await res.json();
      return Array.isArray(data?.events) ? data.events : [];
    },
  });

  if (!workspaceId) return null;
  if (error) return <DataUnavailable message="Workspace activity could not be loaded." retry={refetch} />;

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <div
        className={`flex items-center gap-2 px-4 py-2.5 ${collapsible ? "cursor-pointer hover:bg-stone-50" : ""} border-b border-stone-100 bg-stone-50/60`}
        onClick={() => collapsible && setExpanded((e) => !e)}
      >
        <div className="flex-1 text-[10px] uppercase tracking-wider font-semibold text-stone-500">
          {heading}
        </div>
        {!isLoading && (
          <div className="text-[10px] text-stone-400 font-mono">
            {events.length}{events.length === limit ? "+" : ""}
          </div>
        )}
        {collapsible && (
          expanded
            ? <ChevronDown size={14} className="text-stone-400" aria-hidden />
            : <ChevronRight size={14} className="text-stone-400" aria-hidden />
        )}
      </div>

      {expanded && (
        <div className="divide-y divide-stone-100 max-h-[480px] overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-3 text-xs text-stone-400 italic">Loading…</div>
          )}
          {!isLoading && events.length === 0 && (
            <div className="px-4 py-6 text-center">
              <div className="text-xs text-stone-500">No activity yet.</div>
              <div className="text-[11px] text-stone-400 mt-1">
                Tracked bills, logged touches, designated champions, and other
                workspace mutations will appear here.
              </div>
            </div>
          )}
          {!isLoading && events.map((ev) => {
            const badge = ACTOR_BADGE[ev.actor_type] ?? ACTOR_BADGE.system;
            const BadgeIcon = badge.icon;
            const href = entityHref(ev.subject_type, ev.subject_id);
            return (
              <div key={ev.id} className="px-4 py-2.5 flex items-start gap-3 hover:bg-stone-50">
                <span
                  className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${badge.cls}`}
                  title={ev.mediator ? `${badge.label} · ${ev.mediator}` : badge.label}
                >
                  <BadgeIcon size={11} strokeWidth={2} aria-hidden />
                  <span>{badge.label}</span>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800 leading-snug">
                    {href ? (
                      <a href={href} className="hover:text-action hover:underline underline-offset-2">
                        {ev.summary}
                      </a>
                    ) : (
                      ev.summary
                    )}
                  </div>
                  {ev.detail && (
                    <div className="text-xs text-stone-500 mt-0.5 line-clamp-2">
                      {ev.detail}
                    </div>
                  )}
                </div>
                <span
                  className="shrink-0 text-[11px] text-stone-400 font-mono tabular-nums"
                  title={new Date(ev.created_at).toLocaleString()}
                >
                  {relativeTime(ev.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
